import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  createPaymentMethod,
  createPlaidLink,
  instantPaymentMethod,
  verifyPaymentMethod,
} from '@/services/wallet';

import type {
  TCreateMethodPayload,
  TInstantMethodPayload,
  TVerifyMethodPayload,
} from '@/types/wallet';

import { walletKeys } from '@/queries/wallet';

/**
 * Shared "payment methods changed" behaviour of W9, W10 and W11.
 * What: before the save, cancels calls that are still loading wallet data (an older answer must
 * not land after the save and overwrite fresh data); after the save, marks ALL wallet data old.
 * Why the whole `walletKeys.all`: it covers every payment-method list, including the
 * `{ id: [...] }` list of the station banking section (the old `useCached` invalidation only
 * dropped the list without arguments and missed it) and the auto-reload summary.
 * Active lists refetch at once; lists nobody shows refetch the next time a page opens them.
 * Constraint: `invalidateQueries` is not awaited on purpose. Awaiting it would hold the caller
 * until the refetch finished, which delays closing a modal or moving a wizard on.
 */
const useWalletInvalidation = () => {
  const queryClient = useQueryClient();

  return {
    onMutate: () => queryClient.cancelQueries(walletKeys.all),
    onSuccess: () => {
      queryClient.invalidateQueries(walletKeys.all);
    },
  };
};

/**
 * W8 `createPlaidLink`: asks the backend for a Plaid link token that opens the bank login window.
 * What: resolves to the token string. Plain mutation: it changes no data the portal shows, so
 * nothing is cancelled or invalidated.
 * Used by: `usePaymentMethod` (Add Payment Method modal, create-station bank step).
 * Errors: not handled here; the caller shows its own message and logs.
 */
export const useCreatePlaidLink = () =>
  useMutation({
    mutationFn: () => createPlaidLink().then((response) => response.data.data),
  });

/**
 * W9 `instantPaymentMethod`: saves a bank account the operator picked in Plaid.
 * What: resolves to the new payment method.
 * Cancels before: all wallet queries. Marks old after: all wallet queries (`walletKeys.all`).
 * Used by: `usePaymentMethod` (Plaid success).
 * Errors: not handled here; the caller shows its own message and logs.
 */
export const useInstantPaymentMethod = () =>
  useMutation({
    mutationFn: (payload: TInstantMethodPayload) =>
      instantPaymentMethod(payload).then((response) => response.data.data),
    ...useWalletInvalidation(),
  });

/**
 * W10 `createPaymentMethod`: saves a bank account typed in by hand (manual verification starts).
 * What: resolves to the new payment method.
 * Cancels before: all wallet queries. Marks old after: all wallet queries (`walletKeys.all`).
 * Used by: `usePaymentMethod` (manual bank form).
 * Errors: not handled here; the caller maps known payment-processor errors to messages.
 */
export const useCreatePaymentMethod = () =>
  useMutation({
    mutationFn: (payload: TCreateMethodPayload) =>
      createPaymentMethod(payload).then((response) => response.data.data),
    ...useWalletInvalidation(),
  });

/**
 * W11 `verifyPaymentMethod`: finishes manual bank verification with the two small deposits.
 * What: resolves to the verified payment method.
 * Cancels before: all wallet queries. Marks old after: all wallet queries (`walletKeys.all`).
 * Used by: the (hidden) station banking section, `Verification.tsx`. `mutateAsync` stays pending
 * for the whole call, so the form's submitting state blocks a second submit.
 * Errors: not handled here; the caller shows its own message and logs.
 */
export const useVerifyPaymentMethod = () =>
  useMutation({
    mutationFn: (payload: TVerifyMethodPayload) =>
      verifyPaymentMethod(payload).then((response) => response.data.data),
    ...useWalletInvalidation(),
  });
